'use client';

import { type FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import { isWikiPath } from '../lib/wikiPath';
import { getPortalRoot } from './portalRoot';
import { useLocalTranslation } from '../hooks/useLocalTranslation';
import translations from './TabBar.translations';
import { useHaptic } from '../hooks/useHaptic';
import { useUiMotionEnabled } from '../hooks/useMotion';
import type { TabId } from '../types/tabs';
import { useRuntime } from '../runtime/RuntimeContext';
import { useCapability } from '../runtime/useCapability';
import { withLangPrefix } from '../i18n/locale';
import { useNavigation } from '../contexts/NavigationContext';
import { cn } from '../utils/cn';
import { LANGUAGE_DIR } from '../i18n/types';
import { TAB_URL_MAP } from '../lib/screen-url-map';
import {
  FaHome,
  FaGamepad,
  FaClipboardList,
  FaUser,
  FaUsers,
  FaCog,
  FaBars,
  RiBookOpenLine,
  GiOpenChest,
} from './icons';
import type { IconType } from './icons';

interface TabBarProps {
  activeTab: TabId | null;
  onTabChange: (tabId: TabId) => void;
  onActiveTabClick?: (tabId: TabId) => void;
}

interface MenuItem {
  id: 'profile' | 'referrals' | 'settings' | 'wiki';
  icon: IconType;
  onClick: () => void;
}

interface PlateRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const PLATE_BLEED = 2;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const TabBar: FC<TabBarProps> = ({ activeTab: navigationTab, onTabChange, onActiveTabClick }) => {
  const { t, lang } = useLocalTranslation(translations);
  const { impact } = useHaptic();
  const runtime = useRuntime();
  const pathname = usePathname();
  const onWiki = runtime === 'web' && isWikiPath(pathname);
  const activeTab: TabId | 'wiki' | null = onWiki ? 'wiki' : navigationTab;
  const router = useRouter();
  const wikiLinks = useCapability('wikiLinks');
  const navigation = useNavigation();
  const isRtl = LANGUAGE_DIR[lang] === 'rtl';
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ left: number; right: number; bottom: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const motionEnabled = useUiMotionEnabled();
  const [plate, setPlate] = useState<PlateRect | null>(null);

  const tabs = useMemo(() => {
    return [
      { id: 'balance', icon: FaHome },
      { id: 'cases', icon: GiOpenChest },
      { id: 'games', icon: FaGamepad },
      { id: 'inventory', icon: FaClipboardList },
    ] as const;
  }, []);

  useEffect(() => {
    if (runtime !== 'web' || onWiki) return;
    for (const tab of tabs) {
      router.prefetch(TAB_URL_MAP[tab.id]);
    }
  }, [tabs, router, runtime, onWiki]);

  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

  useIsomorphicLayoutEffect(() => {
    const row = rowRef.current;
    const button = activeIndex >= 0 ? tabRefs.current[activeIndex] : null;
    if (!row || !button) return;

    const measure = () => {
      const rowBox = row.getBoundingClientRect();
      const buttonBox = button.getBoundingClientRect();
      const next: PlateRect = {
        x: buttonBox.left - rowBox.left - PLATE_BLEED,
        y: buttonBox.top - rowBox.top,
        width: buttonBox.width + PLATE_BLEED * 2,
        height: buttonBox.height,
      };
      setPlate((prev) =>
        prev && prev.x === next.x && prev.y === next.y && prev.width === next.width && prev.height === next.height
          ? prev
          : next
      );
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const tab of tabRefs.current) if (tab) observer.observe(tab);
    return () => observer.disconnect();
  }, [activeIndex, tabs, lang]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (menuRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const reanchor = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuAnchor({
        left: rect.left,
        right: window.innerWidth - rect.right,
        bottom: window.innerHeight - rect.top,
      });
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reanchor);
    window.addEventListener('scroll', reanchor, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reanchor);
      window.removeEventListener('scroll', reanchor, true);
    };
  }, [menuOpen]);

  const handleTabChange = (tabId: TabId) => {
    if (tabId !== activeTab) {
      impact('soft');
      onTabChange(tabId);
    } else if (onActiveTabClick) {
      impact('soft');
      onActiveTabClick(tabId);
    }
  };

  const handleMenuToggle = () => {
    impact('soft');
    setMenuOpen((open) => {
      const next = !open;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuAnchor({
          left: rect.left,
          right: window.innerWidth - rect.right,
          bottom: window.innerHeight - rect.top,
        });
      }
      return next;
    });
  };

  const runMenuAction = (action: () => void) => {
    impact('soft');
    setMenuOpen(false);
    action();
  };

  const menuItems: MenuItem[] = [
    {
      id: 'profile',
      icon: FaUser,
      onClick: () => runMenuAction(() => onTabChange('profile')),
    },
    {
      id: 'referrals',
      icon: FaUsers,
      onClick: () => runMenuAction(() => onTabChange('referrals')),
    },
    {
      id: 'settings',
      icon: FaCog,
      onClick: () => runMenuAction(() => navigation.navigate('settings')),
    },
    // WHY: адрес с языком и в Mini App — без него слой вики не откроется, proxy уведёт на корень
    ...(wikiLinks && lang !== 'fa'
      ? [{ id: 'wiki' as const, icon: RiBookOpenLine, onClick: () => runMenuAction(() => router.push(withLangPrefix(lang, '/wiki'))) }]
      : []),
  ];

  const menuActive = menuItems.some((item) => item.id === activeTab);

  return (
    <div data-sound="ring" className="flex justify-center items-stretch gap-3 pt-4 px-[10px] pb-[max(25px,var(--tg-viewport-safe-area-inset-bottom,0px))] z-[1000]">
      <div ref={rowRef} className="relative flex items-start justify-center flex-1 min-w-px px-[2px]">
        <div
          aria-hidden
          className="liquid-glass absolute -inset-1 rounded-full pointer-events-none backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125"
        />
        {plate && (
          <span
            aria-hidden
            className={cn(
              'liquid-glass-selection pointer-events-none absolute start-0 top-0 rounded-full',
              motionEnabled && 'shot-tabbar-indicator'
            )}
            style={{
              transform: `translate3d(${plate.x}px, ${plate.y}px, 0)`,
              width: plate.width,
              height: plate.height,
              opacity: activeIndex >= 0 ? 1 : 0,
            }}
          />
        )}
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              onClick={() => handleTabChange(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'group relative flex flex-1 min-w-px flex-col items-center justify-center gap-px pt-[6px] pb-[7px] px-[8px]',
                'bg-transparent border-none cursor-pointer outline-none [-webkit-tap-highlight-color:transparent]'
              )}
            >
              <span
                className={cn(
                  'relative flex items-center justify-center h-[28px] w-full text-[24px] font-[590]',
                  'text-vibrant-label-primary group-active:opacity-60',
                  motionEnabled && 'shot-press-fade'
                )}
              >
                <Icon />
              </span>

              <span
                className={cn(
                  'relative max-w-[calc(100%+4px)] truncate p-px -m-px',
                  'text-[10px] leading-[12px] font-[590] tracking-[-0.1px] text-center',
                  'text-vibrant-label-primary group-active:opacity-60',
                  motionEnabled && 'shot-press-fade'
                )}
              >
                {t(tab.id)}
              </span>
            </button>
          );
        })}
      </div>

      <div ref={menuRef} className="relative flex self-start items-start">
        <div
          aria-hidden
          className="liquid-glass absolute -inset-1 rounded-full pointer-events-none backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125"
        />
        <button
          ref={buttonRef}
          type="button"
          aria-label={t('menu')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={handleMenuToggle}
          aria-current={menuActive ? 'page' : undefined}
          className={cn(
            'group relative flex h-[54px] w-[54px] items-center justify-center rounded-full',
            'bg-transparent border-none cursor-pointer outline-none [-webkit-tap-highlight-color:transparent]'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'liquid-glass-selection pointer-events-none absolute inset-0 rounded-full',
              motionEnabled && 'shot-tabbar-menu-indicator'
            )}
            style={{ opacity: menuActive ? 1 : 0 }}
          />
          <FaBars
            className={cn(
              'relative text-[24px] font-[590] text-vibrant-label-primary group-active:opacity-60',
              motionEnabled && 'shot-press-fade'
            )}
          />
        </button>
      </div>

      {menuOpen && menuAnchor && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role="menu"
            data-sound="ring"
            style={{
              position: 'fixed',
              ...(isRtl ? { left: menuAnchor.left } : { right: menuAnchor.right }),
              bottom: menuAnchor.bottom + 16,
            }}
            className={cn(
              'z-[2147483000] min-w-[220px]',
              'rounded-[18px] py-1.5 px-1.5',
              'bg-[rgba(28,28,30,0.92)] [-webkit-backdrop-filter:blur(24px)_saturate(180%)] backdrop-blur-[24px] backdrop-saturate-[180%]',
              'shadow-[0_12px_36px_rgba(0,0,0,0.45)] border border-white/[0.08]',
              '[animation:menuPop_120ms_ease-out_both] origin-bottom-right rtl:origin-bottom-left'
            )}
          >
            {menuItems.map((item, idx) => {
              const Icon = item.icon;
              const isLast = idx === menuItems.length - 1;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={item.onClick}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2.5 rounded-[12px] cursor-pointer',
                    'bg-transparent border-none outline-none [-webkit-tap-highlight-color:transparent]',
                    'text-[16px] leading-[20px] font-[510] tracking-[-0.2px] text-white',
                    'hover:bg-white/[0.06] active:bg-white/[0.1]',
                    !isLast && 'mb-[1px]'
                  )}
                >
                  <span className="flex-1 text-start">
                    {t(item.id)}
                  </span>
                  <Icon
                    className={cn(
                      'text-[18px] shrink-0',
                      'text-white/70'
                    )}
                  />
                </button>
              );
            })}
          </div>,
          getPortalRoot(),
        )}
    </div>
  );
};
